/**
 * Module dependencies.
 */

import { Button } from 'app/components/ui/button';
import { VenetianMask } from 'lucide-react';
import { appRoutes } from 'app/routes';
import { useMe } from 'app/hooks/requests/use-me';
import { useStopImpersonating } from 'app/hooks/requests/use-admin-users';
import { useTranslation } from 'react-i18next';
import React from 'react';

/**
 * Export `ImpersonationBanner` component.
 *
 * Shown on every page while an admin is impersonating another user, with a way
 * back to their own session.
 */

export function ImpersonationBanner(): JSX.Element | null {
  const [translate] = useTranslation();
  const { data: me } = useMe();
  const stop = useStopImpersonating();

  if (!me?.isImpersonating) {
    return null;
  }

  const name = [me.firstName, me.lastName].filter(Boolean).join(' ') || me.email;

  const onStop = async () => {
    await stop.mutateAsync();

    // Full reload so the whole app returns to the admin's session.
    window.location.href = appRoutes.dashboard.home.base;
  };

  return (
    <div
      className={
        'mb-4 flex items-center justify-between gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900'
      }
    >
      <div className={'flex min-w-0 items-center gap-2'}>
        <VenetianMask className={'h-4 w-4 shrink-0'} />

        <span className={'truncate'}>{translate('administration.impersonatingBanner', { name })}</span>
      </div>

      <Button
        className={'shrink-0 border-amber-300 bg-transparent text-amber-900 hover:bg-amber-100'}
        disabled={stop.isLoading}
        onClick={onStop}
        size={'sm'}
        variant={'outline'}
      >
        {translate('administration.stopImpersonating')}
      </Button>
    </div>
  );
}
